import { SyntheticEvent, useCallback, useEffect, useState } from "react";
import { User } from "firebase/auth";
import "../styles/albumshub.css"
import { addAlbum, deleteAlbum, getAlbumsById, getCollaboratorAlbums, getUserAlbums } from "../firestore/db";
import { uploadFile } from "../storage/storage";
import { Album } from "../firestore/firestore-types";
import { AuthProvider } from "../contexts/authContext";
import AuthPage, { logOut } from "./auth";
import { Link } from "react-router-dom";
import AlbumCard from "./components/albumCard";
import { DEFAULT_AVATAR_URL, resolveImageUrl } from "../utils/image";

interface AlbumsHubProps {
    user: User | null;
}

const AlbumsHub = (props: AlbumsHubProps) => {
    const user = props.user;
    const [albumIds, setAlbumIds] = useState<string[] | undefined>([]);
    const [albums, setAlbums] = useState<Album[]>([]);
    const [collabAlbums, setCollabAlbums] = useState<Album[]>([]);
    const [albumsFinal, setAlbumsFinal] = useState<Album[]>([]);
    const [newAlbumName, setNewAlbumName] = useState("");
    const [newAlbumDescription, setNewAlbumDescription] = useState("");
    const [newAlbumCoverPhoto, setNewAlbumCoverPhoto] = useState<string | undefined>(undefined);
    const [newAlbumCollaborators, setNewAlbumCollaborators] = useState<string[]>([]);
    const [newAlbumSharedWith, setNewAlbumSharedWith] = useState<string[]>([]);
    const [newAlbumPublic, setNewAlbumPublic] = useState(false);
    const [addAlbumWindow, setAddAlbumWindow] = useState(false);

    const resetAlbumDraft = () => {
        setNewAlbumName("");
        setNewAlbumDescription("");
        setNewAlbumCoverPhoto(undefined);
        setNewAlbumCollaborators([]);
        setNewAlbumSharedWith([]);
        setNewAlbumPublic(false);
    }

    const getAlbumsIds = useCallback(() => {
        if (!user?.uid) {
            setAlbumIds([]);
            return;
        }

        getUserAlbums(user.uid).then((Ids) => {
            if (Ids === null) {
                setAlbumIds([]);
                return;
            }
            setAlbumIds(Ids);
        });
    }, [user?.uid])

    useEffect(() => {
        if (!user?.uid || !user?.email) {
            return;
        }

        getAlbumsIds();
        resetAlbumDraft();

        getCollaboratorAlbums(user?.uid!, user?.email!).then((docs) => {
            if (docs !== "nosuchalbum" && docs !== "notowner" && docs !== null && docs !== undefined) {
                setCollabAlbums(docs);
            } else {
                setCollabAlbums([]);
            }
        });
    }, [getAlbumsIds, user])

    useEffect(() => {
        if (!user?.uid || !user?.email) {
            setAlbums([]);
            return;
        }

        if (albumIds?.length) {
            getAlbumsById(user.uid, albumIds, user.email).then((docs) => {
                if (docs !== "nosuchalbum" && docs !== "notowner") {
                    setAlbums(docs);
                }
            })
        } else {
            setAlbums([]);
        }
    }, [albumIds, user])

    useEffect(() => {
        setAlbumsFinal(albums.concat(collabAlbums));
    }, [albums, collabAlbums])

    const handleAddAlbum = () => {
        if (!user?.uid || newAlbumName.trim() === "") {
            alert("Album name cannot be empty");
            return;
        }

        addAlbum(user?.uid!, newAlbumName, newAlbumPublic, newAlbumDescription, newAlbumCoverPhoto, newAlbumSharedWith, newAlbumCollaborators).then((s) => {
            if (s === "success") {
                resetAlbumDraft();
                toggleAddAlbumWindow();
                getAlbumsIds();
            }
        });
    }

    const handleDeleteAlbum = (album: Album) => {
        const albumPath = "users/" + album.owner + "/albums/" + album.name + "-" + user?.uid + "/";
        deleteAlbum(user?.uid!, album.albumId, albumPath).then((s) => {
            if (s === "success-owner") {
                setAlbumsFinal(albumsFinal.filter((a) => a.albumId !== album.albumId));
            } else if (s === "success-collaborator") {
                setAlbumsFinal(albumsFinal.filter((a) => a.albumId !== album.albumId));
            } else {
                console.error("error deleting album: ", s);
            }
        });
    }

    const toggleAddAlbumWindow = () => {
        resetAlbumDraft();
        setAddAlbumWindow((prev) => !prev);
    }

    const handleImageError = (event: SyntheticEvent<HTMLImageElement>) => {
        event.currentTarget.src = DEFAULT_AVATAR_URL;
    }

    if (props.user !== null) {
        return (
            <div className="albums-hub-wrapper">
                <header className="pageheader-albumshub">
                    <div className="hub-heading">
                        <p className="hub-kicker">hanimcilar</p>
                        <h1>Albums Hub</h1>
                        <p className="hub-subtitle">Create and share themed polaroid albums with privacy and collaboration controls.</p>
                    </div>

                    <div className="pageheader-user">
                        <img
                            src={resolveImageUrl(user?.photoURL, DEFAULT_AVATAR_URL)}
                            alt="user profile"
                            referrerPolicy="no-referrer"
                            onError={handleImageError}
                        />
                        <div className="hub-user-copy">
                            <h2>{user?.displayName || "Anonymous User"}</h2>
                            <p>{user?.email}</p>
                        </div>
                        <div className="hub-user-actions">
                            <Link className="hub-action-link" to="/settings">Settings</Link>
                            <button className="hub-action-button" onClick={() => logOut()}>Sign Out</button>
                        </div>
                    </div>
                </header>

                <section className="hub-content">
                    <div className="hub-content-head">
                        <p>{albumsFinal.length} album{albumsFinal.length === 1 ? "" : "s"} visible</p>
                    </div>
                    <div className="album-cards-list">
                    <div className="add-album-card" onClick={toggleAddAlbumWindow}> 
                        <div className="add-album-card-plus-sign">
                            <h1>+</h1>
                        </div>
                        <div className="add-album-card-info">
                            <h2>Add New Album</h2>
                            <p>Create a new board with title, cover, and sharing rules.</p>
                        </div>
                    </div>
                        <div className={`add-album-popup ${addAlbumWindow ? 'visible' : ''}`}>
                            <div className="add-album-popup-content">
                                <div className="add-album-popup-header">
                                    <h1>Add New Album</h1>
                                </div>
                                <div className="add-album-popup-body">
                                    <div className="add-album-popup-body-inputs">
                                        <div className="add-album-popup-body-inputs-album-name">
                                            <input
                                                type="text"
                                                placeholder="Album name"
                                                required
                                                value={newAlbumName}
                                                onChange={(e) => setNewAlbumName(e.target.value)}
                                            />
                                        </div>
                                        <div className="add-album-popup-body-inputs-album-description">
                                            <input
                                                type="text"
                                                placeholder="Album description"
                                                value={newAlbumDescription}
                                                onChange={(e) => setNewAlbumDescription(e.target.value)}
                                            />
                                        </div>
                                        {newAlbumName &&
                                            <div className="add-album-popup-body-inputs-album-cover-photo">
                                                <label htmlFor="new-album-cover-photo">Cover photo</label>
                                                <input
                                                    id="new-album-cover-photo"
                                                    type="file"
                                                    onChange={(e) => {
                                                        if (!e.target.files || !e.target.files[0]) {
                                                            return;
                                                        }
                                                        const fileUploadAlbumName = newAlbumName + "-" + user?.uid;
                                                        uploadFile(e.target.files[0], "users/" + user?.uid! + "/albums/" + fileUploadAlbumName, "AlbumCover").then((downloadUrl) => {
                                                            if (downloadUrl) {
                                                                setNewAlbumCoverPhoto(downloadUrl);
                                                            }
                                                        })
                                                    }}
                                                />
                                                {newAlbumCoverPhoto &&
                                                    <img className="cover-preview" src={newAlbumCoverPhoto} alt={`album-cover-${newAlbumName}`} />
                                                }
                                            </div>
                                        }

                                        <div className="add-album-popup-body-inputs-album-collaborators">
                                            <input
                                                type="text"
                                                placeholder="Collaborators (email, comma separated)"
                                                value={newAlbumCollaborators.join(", ")}
                                                onChange={(e) => setNewAlbumCollaborators(e.target.value.split(',').map((item) => item.trim()).filter(Boolean))}
                                            />
                                        </div>
                                        <div className="add-album-popup-body-inputs-album-shared-with">
                                            <input
                                                type="text"
                                                placeholder="Shared with (email, comma separated)"
                                                value={newAlbumSharedWith.join(", ")}
                                                onChange={(e) => setNewAlbumSharedWith(e.target.value.split(',').map((item) => item.trim()).filter(Boolean))}
                                            />
                                        </div>
                                        <div className="add-album-popup-body-inputs-album-public-private">
                                            <label htmlFor="public-checkbox">Public album</label>
                                            <input
                                                type="checkbox"
                                                id="public-checkbox"
                                                checked={newAlbumPublic}
                                                onChange={(e) => setNewAlbumPublic(e.target.checked)}
                                            />
                                        </div>
                                    </div>
                                    <div className="add-album-popup-body-buttons">
                                        <button onClick={toggleAddAlbumWindow}>Cancel</button>
                                        <button onClick={handleAddAlbum}>Create Album</button>
                                    </div>
                                </div>
                            </div>
                        </div>
                    {albumsFinal && albumsFinal?.map((album) => {
                        return (
                            <AlbumCard key={album.albumId} album={album} handleDeleteAlbum={handleDeleteAlbum} />
                        )
                    })}
                    </div>
                </section>
                </div>
    );
    } else { 
        return (
                <div className="albums-hub-wrapper guest-hub">
                    <div className="top-wrapped">
                        <p className="hub-kicker">hanimcilar</p>
                        <h1>Albums Hub</h1>
                        <h2>Sign in to view your boards, upload moments, and share your story.</h2>
                    </div>
                    <AuthProvider>
                        <AuthPage user={props.user} />
                    </AuthProvider>
                </div>
        );
    }
}

export default AlbumsHub;